import { useEffect, useState } from 'react'
import { useSaveSettings, useSettings } from '../api/hooks.ts'
import { useAuth } from '../auth/AuthContext.tsx'
import { Button, Card, ErrorNote, Screen, SectionTitle, Spinner } from '../components/ui.tsx'
import { NotificationsCard } from '../components/NotificationsCard.tsx'
import { eur } from '../lib/format.ts'
import type { Settings } from '../api/types.ts'

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="flex flex-col gap-1 border-b border-[var(--color-line)] py-3 last:border-b-0">
      <span className="text-[15px]">{label}</span>
      {children}
      {hint && <span className="text-xs text-[var(--color-muted)]">{hint}</span>}
    </label>
  )
}

const inputClass =
  'rounded-lg border border-[var(--color-line)] bg-[var(--color-canvas)] px-3 py-2 text-[16px]'

export function SettingsScreen() {
  const { user, logout } = useAuth()
  const settings = useSettings()
  const save = useSaveSettings()
  const [draft, setDraft] = useState<Settings | null>(null)

  useEffect(() => {
    if (settings.data) setDraft(settings.data)
  }, [settings.data])

  if (settings.isLoading || !draft) return <Spinner />

  const isAdmin = user?.role === 'ADMIN'
  const set = <K extends keyof Settings>(key: K, value: Settings[K]) =>
    setDraft({ ...draft, [key]: value })

  const maxDeduction =
    draft.deductionCapPct > 0
      ? (draft.weeklyAllowanceCents * draft.deductionCapPct) / 100
      : draft.weeklyAllowanceCents

  return (
    <Screen>
      <h1 className="mb-4 text-xl font-bold tracking-tight">Ajustes</h1>

      <NotificationsCard />

      {!isAdmin ? (
        <Card>
          <p className="text-sm text-[var(--color-muted)]">
            Solo los administradores pueden cambiar la configuración. Estos son los valores
            actuales:
          </p>
          <ul className="mt-3 space-y-1 text-sm">
            <li>Asignación semanal: {eur(draft.weeklyAllowanceCents)}</li>
            <li>Avisos para marcar no hecha: {draft.warningThreshold}</li>
            <li>Pérdida máxima por semana: {eur(maxDeduction)}</li>
            <li>
              Máximo de bonus:{' '}
              {draft.weeklyBonusCapCents > 0 ? eur(draft.weeklyBonusCapCents) : 'sin límite'}
            </li>
          </ul>
        </Card>
      ) : (
        <>
          {save.error != null && <ErrorNote error={save.error} />}

          <SectionTitle>Dinero</SectionTitle>
          <Card>
            <Field label="Asignación semanal (€)">
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                className={inputClass}
                value={(draft.weeklyAllowanceCents / 100).toFixed(2)}
                onChange={(e) =>
                  set('weeklyAllowanceCents', Math.round(Number(e.target.value) * 100) || 0)
                }
              />
            </Field>
            <Field
              label="Tope de deducción (%)"
              hint={`Pérdida máxima ${eur(maxDeduction)} · cobro mínimo ${eur(draft.weeklyAllowanceCents - maxDeduction)}. 0 desactiva el tope.`}
            >
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={100}
                className={inputClass}
                value={draft.deductionCapPct}
                onChange={(e) => set('deductionCapPct', Number(e.target.value) || 0)}
              />
            </Field>
            <Field label="Techo de bonus por semana (€)" hint="0 = sin techo.">
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                className={inputClass}
                value={(draft.weeklyBonusCapCents / 100).toFixed(2)}
                onChange={(e) =>
                  set('weeklyBonusCapCents', Math.round(Number(e.target.value) * 100) || 0)
                }
              />
            </Field>
          </Card>

          <SectionTitle>Avisos</SectionTitle>
          <Card>
            <Field label="Avisos antes de marcar como no hecha">
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={10}
                className={inputClass}
                value={draft.warningThreshold}
                onChange={(e) => set('warningThreshold', Number(e.target.value) || 1)}
              />
            </Field>
            <Field
              label="Minutos mínimos entre avisos"
              hint="Evita gastar los tres avisos seguidos sin dar margen de reacción."
            >
              <input
                type="number"
                inputMode="numeric"
                min={0}
                className={inputClass}
                value={draft.warningCooldownMinutes}
                onChange={(e) => set('warningCooldownMinutes', Number(e.target.value) || 0)}
              />
            </Field>
          </Card>

          <SectionTitle>Familia</SectionTitle>
          <Card>
            <label className="flex items-center justify-between py-2">
              <span className="text-[15px]">Los hermanos ven la semana del otro</span>
              <input
                type="checkbox"
                className="h-6 w-6"
                checked={draft.siblingsCanViewEachOther}
                onChange={(e) => set('siblingsCanViewEachOther', e.target.checked)}
              />
            </label>
          </Card>

          <Button
            className="mt-5 w-full"
            disabled={save.isPending}
            onClick={() => save.mutate(draft)}
          >
            {save.isPending ? 'Guardando…' : 'Guardar cambios'}
          </Button>
          {save.isSuccess && (
            <p className="mt-2 text-center text-sm text-[var(--color-good)]">Guardado ✓</p>
          )}
          <p className="mt-2 text-center text-xs text-[var(--color-muted)]">
            Los cambios sólo afectan a las semanas abiertas. Las cerradas quedan como estaban.
          </p>
        </>
      )}

      <Button variant="secondary" className="mt-8 w-full" onClick={() => void logout()}>
        Cerrar sesión
      </Button>
    </Screen>
  )
}
